import React, { useEffect, useState } from "react";
import axios from "axios";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { setAllPromo } from "../store/slices/user";
import { useConfig } from "../contexts/ConfigContext";
import PromotionCard from "../components/PromotionCard";

// Declare the shared promise at module scope
let promotionsFetchPromise = null;

// Promotion component
const Promotion = () => {
  const dispatch = useDispatch();
  const { apiBaseUrl } = useConfig();
  const allPromo = useSelector((state) => state.user.allPromo);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const promotionCount = allPromo?.length || 0;

  // Fetch promotions from the server
  useEffect(() => {
    let cancelled = false;

    if (!promotionsFetchPromise) {
      promotionsFetchPromise = axios
        .get("https://api.bucket-001.online/promotion/getavailable")
        .finally(() => {
          promotionsFetchPromise = null;
        });
    }

    promotionsFetchPromise
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.data || res?.data || [];
        dispatch(setAllPromo(list));
      })
      .catch(() => {
        if (cancelled) return;
        dispatch(setAllPromo([]));
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);
  // Resolve image URL
  const resolveImg = (url) => {
    if (!url) return "";
    return url.startsWith("http") ? url : `${apiBaseUrl}${url}`;
  };

  // Helpers for display
  // Inline SVG placeholder to avoid network calls and infinite onError loops
  const makeSvgPlaceholder = (w, h, text = "Promotion Image") => {
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}'><rect width='100%' height='100%' fill='#2d2d2d'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#f59e0b' font-family='sans-serif' font-size='16'>${text}</text></svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  };
  const getImg = (promo, w = 800, h = 300) => {
    const url = promo?.imageUrl;
    return url ? resolveImg(url) : makeSvgPlaceholder(w, h);
  };
  const bonusText = (p) => {
    if (!p) return "";
    const amt = p.BonusAmount;
    if (p.BonusAmountType && p.BonusAmountType.includes("%")) {
      const max = p.BonusMaximum != null ? ` สูงสุด ${p.BonusMaximum}` : "";
      return `โบนัส ${amt}%${max}`;
    }
    if (typeof amt === "number") {
      const max = p.BonusMaximum != null ? ` (สูงสุด ${p.BonusMaximum})` : "";
      return `รับเพิ่ม ${amt}${max}`;
    }
    return "";
  };
  const depositText = (p) => {
    if (!p) return "";
    if (p.BonusDepMinimum === p.BonusDepMaximum)
      return `ฝาก ${p.BonusDepMinimum}`;
    if (p.BonusDepMinimum != null && p.BonusDepMaximum != null)
      return `ฝาก ${p.BonusDepMinimum}-${p.BonusDepMaximum}`;
    if (p.BonusDepMinimum != null) return `ฝากขั้นต่ำ ${p.BonusDepMinimum}`;
    return "";
  };

  const handleReadMore = (promotion) => {
    setSelectedPromotion(promotion);
  };

  const handleBackToList = () => {
    setSelectedPromotion(null);
  };

  // Default list view
  return (
    <div
      style={{
        padding: "16px 0 40px",
        minHeight: "100vh",
        background: "transparent",
      }}
    >
      <div
        style={{
          margin: "0 auto",
          width: "min(1320px, calc(100% - 28px))",
          padding: "20px 14px",
          border: "1px solid var(--primaryColor)",
          borderRadius: "22px",
          background:
            "linear-gradient(165deg, color-mix(in srgb, var(--primaryColor) 18%, rgba(10,10,14,0.94)) 0%, color-mix(in srgb, var(--primaryColor) 10%, rgba(10,10,14,0.9)) 48%, rgba(10,10,14,0.94) 100%)",
          backdropFilter: "blur(16px) saturate(120%)",
          boxShadow:
            "0 16px 40px rgba(0,0,0,0.55), 0 0 48px color-mix(in srgb, var(--primaryColor) 22%, transparent)",
        }}
      >
        <div className="page-container">
          <div
            className="header-section"
            style={{
              position: "relative",
              overflow: "hidden",
              marginBottom: "14px",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "-50%",
                left: "50%",
                transform: "translateX(-50%)",
                width: 320,
                height: 180,
                borderRadius: "50%",
                background: "var(--primaryColor)",
                filter: "blur(74px)",
                opacity: 0.16,
                pointerEvents: "none",
                animation: "promoHeaderPulse 4s ease-in-out infinite",
              }}
            />

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                position: "relative",
                zIndex: 1,
                padding: "6px 4px",
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 14,
                  flexShrink: 0,
                  background:
                    "linear-gradient(145deg, var(--primaryColor), color-mix(in srgb, var(--primaryColor) 60%, #000))",
                  boxShadow:
                    "0 6px 20px color-mix(in srgb, var(--primaryColor) 40%, transparent)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Icon
                  icon="fluent-mdl2:giftbox-open"
                  width={28}
                  height={28}
                  style={{ position: "relative", zIndex: 1 }}
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: 14,
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, transparent 55%)",
                    pointerEvents: "none",
                  }}
                />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <h1
                  className="page-title"
                  style={{
                    margin: 0,
                    fontSize: 22,
                    fontWeight: 900,
                    color: "#fff",
                    letterSpacing: "0.01em",
                    lineHeight: 1.25,
                    textShadow:
                      "0 4px 18px color-mix(in srgb, var(--primaryColor) 30%, transparent)",
                  }}
                >
                  โปรโมชั่นทั้งหมด
                </h1>
                <div
                  style={{
                    marginTop: 6,
                    color: "rgba(255,255,255,0.72)",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  รวมโปรฝากรับโบนัสและสิทธิพิเศษที่เลือกใช้งานได้จากหน้าเดียว
                </div>
              </div>

              <div
                style={{
                  padding: "5px 12px",
                  borderRadius: 999,
                  flexShrink: 0,
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "rgba(255,255,255,0.76)",
                  fontSize: 12,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <Icon
                  icon="solar:gift-bold-duotone"
                  width={15}
                  height={15}
                  style={{ opacity: 0.78 }}
                />
                {promotionCount} รายการ
              </div>
            </div>

            <div
              style={{
                marginTop: 12,
                height: 1,
                background:
                  "linear-gradient(90deg, transparent 0%, color-mix(in srgb, var(--primaryColor) 45%, transparent) 50%, transparent 100%)",
              }}
            />
          </div>

          <div className="promotion-grid">
            {allPromo?.map((p) => (
              <PromotionCard
                key={p.PromotionID}
                promotion={p}
                onClick={handleReadMore}
                getImg={getImg}
                makeSvgPlaceholder={makeSvgPlaceholder}
                bonusText={bonusText}
                depositText={depositText}
              />
            ))}
            {promotionCount === 0 && (
              <div className="promotion-empty-state">
                <div className="promotion-empty-state__icon">📭</div>
                <h3>ไม่มีโปรโมชั่นในขณะนี้</h3>
                <p>เมื่อมีรายการใหม่ ระบบจะแสดงในหน้านี้โดยอัตโนมัติ</p>
              </div>
            )}
          </div>

          {selectedPromotion && (
            <div
              className="promotion-popup-overlay"
              onClick={handleBackToList}
              role="presentation"
            >
              <div
                className="promotion-popup-dialog"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={selectedPromotion.Detail || "promotion-image-popup"}
              >
                <button
                  type="button"
                  className="promotion-popup-close"
                  onClick={handleBackToList}
                  aria-label="close-promotion-popup"
                >
                  <Icon
                    icon="line-md:close-circle-filled"
                    width={28}
                    height={28}
                  />
                </button>
                <img
                  className="promotion-popup-image"
                  src={getImg(selectedPromotion, 1400, 2000)}
                  alt={selectedPromotion.Detail}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = makeSvgPlaceholder(1400, 2000);
                  }}
                />
              </div>
            </div>
          )}

          <style>{`
            @keyframes promoHeaderPulse {
              0%, 100% { opacity: 0.14; transform: translateX(-50%) scale(1); }
              50% { opacity: 0.22; transform: translateX(-50%) scale(1.15); }
            }

            .page-container {
              padding: 24px;
            }

            .promo-overview {
              display: grid;
              grid-template-columns: minmax(0, 1.6fr) minmax(260px, 0.9fr);
              gap: 18px;
              margin: 4px 0 26px;
            }

            .promo-callout {
              display: flex;
              align-items: center;
              gap: 14px;
              border-radius: 18px;
              padding: 18px 20px;
              border: 1px solid rgba(255,255,255,0.12);
              box-shadow: 0 10px 26px rgba(0,0,0,0.24);
            }

            .promo-callout--primary {
              background: linear-gradient(135deg, color-mix(in srgb, var(--primaryColor) 28%, rgba(255,255,255,0.08)) 0%, rgba(255,255,255,0.07) 100%);
            }

            .promo-callout__icon {
              width: 54px;
              height: 54px;
              border-radius: 16px;
              background: linear-gradient(145deg, var(--primaryColor), color-mix(in srgb, var(--primaryColor) 72%, #000));
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
              font-size: 25px;
              box-shadow: 0 8px 20px color-mix(in srgb, var(--primaryColor) 32%, transparent);
            }

            .promo-callout__content {
              flex: 1;
              min-width: 0;
            }

            .promo-callout__content h2 {
              margin: 0;
              color: #fff;
              font-size: 18px;
              font-weight: 800;
            }

            .promo-callout__content p {
              margin: 6px 0 0;
              color: rgba(255,255,255,0.78);
              font-size: 13px;
              line-height: 1.5;
            }

            .promo-callout__badge {
              flex-shrink: 0;
              border-radius: 999px;
              padding: 6px 12px;
              background: rgba(255,255,255,0.08);
              border: 1px solid rgba(255,255,255,0.12);
              color: rgba(255,255,255,0.8);
              font-size: 12px;
              font-weight: 700;
            }

            .promo-stats-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 14px;
            }

            .promo-stat-card {
              border-radius: 18px;
              padding: 18px 18px;
              background: linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%);
              border: 1px solid rgba(255,255,255,0.1);
              display: flex;
              flex-direction: column;
              justify-content: center;
              gap: 8px;
              box-shadow: 0 10px 24px rgba(0,0,0,0.18);
            }

            .promo-stat-label {
              color: rgba(255,255,255,0.66);
              font-size: 12px;
              font-weight: 700;
            }

            .promo-stat-value {
              color: #fff;
              font-size: 22px;
              font-weight: 900;
              line-height: 1;
            }

            .promotion-grid {
              display: grid;
              gap: 20px;
              grid-template-columns: repeat(4, minmax(0, 1fr));
            }

            .promotion-empty-state {
              grid-column: 1 / -1;
              min-height: 260px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: 10px;
              border-radius: 20px;
              background: linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%);
              border: 1px solid rgba(255,255,255,0.12);
              color: rgba(255,255,255,0.8);
              text-align: center;
              padding: 24px;
            }

            .promotion-empty-state__icon {
              font-size: 42px;
            }

            .promotion-empty-state h3 {
              margin: 0;
              color: #fff;
              font-size: 20px;
            }

            .promotion-empty-state p {
              margin: 0;
              max-width: 420px;
              font-size: 14px;
              line-height: 1.6;
            }

            .promotion-popup-overlay {
              position: fixed;
              inset: 0;
              background: rgba(0, 0, 0, 0.78);
              backdrop-filter: blur(8px);
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 24px;
              z-index: 1600;
            }

            .promotion-popup-dialog {
              position: relative;
              max-width: min(92vw, 980px);
              max-height: 90vh;
              border-radius: 22px;
              overflow: hidden;
              background: linear-gradient(180deg, rgba(15,15,18,0.96) 0%, rgba(10,10,14,0.98) 100%);
              border: 1px solid rgba(255,255,255,0.16);
              box-shadow: 0 24px 80px rgba(0,0,0,0.55);
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .promotion-popup-close {
              position: absolute;
              top: 12px;
              right: 12px;
              width: 42px;
              height: 42px;
              border: none;
              border-radius: 999px;
              background: rgba(0,0,0,0.55);
              color: #fff;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              z-index: 2;
              box-shadow: 0 8px 24px rgba(0,0,0,0.35);
              transition: transform 0.2s ease, background 0.2s ease;
            }

            .promotion-popup-close:hover {
              transform: scale(1.06);
              background: rgba(0,0,0,0.72);
            }

            .promotion-popup-image {
              display: block;
              max-width: 100%;
              max-height: 90vh;
              width: auto;
              height: auto;
              object-fit: contain;
              background: #0f0f12;
            }

            @media (min-width: 768px) {
              .page-container {
                padding: 32px;
              }
            }

            @media (max-width: 1180px) {
              .promotion-grid {
                grid-template-columns: repeat(3, minmax(0, 1fr));
              }
            }

            @media (max-width: 960px) {
              .promo-overview {
                grid-template-columns: 1fr;
              }

              .promotion-grid {
                grid-template-columns: repeat(2, minmax(0, 1fr));
              }
            }

            @media (max-width: 768px) {
              .header-section {
                margin-bottom: 10px !important;
              }

              .page-title {
                font-size: 24px !important;
              }

              .promo-callout {
                flex-direction: column;
                align-items: flex-start;
                text-align: left;
              }

              .promo-callout__badge {
                align-self: flex-start;
              }

              .promo-stats-grid {
                grid-template-columns: 1fr;
              }

              .promotion-grid {
                grid-template-columns: 1fr;
                gap: 16px;
              }

              .promotion-popup-overlay {
                align-items: flex-start;
                padding: 18px 14px 14px;
              }

              .promotion-popup-dialog {
                max-width: 100%;
                border-radius: 16px;
              }

              .promotion-popup-close {
                top: 8px;
                right: 8px;
                width: 38px;
                height: 38px;
              }
            }
          `}</style>
        </div>
      </div>
    </div>
  );
};

export default Promotion;
